import { Controller } from "@hotwired/stimulus"
import PhotoSwipe from "photoswipe"

export default class extends Controller {
  static targets = ["gallery"]
  static values = { fieldTitle: String }

  connect() {
    this.loadCSS()
    if (this.hasGalleryTarget) {
      this._handleClick = this.handleGalleryClick.bind(this)
      this.galleryTarget.addEventListener("click", this._handleClick)
    }
  }

  disconnect() {
    if (this.hasGalleryTarget && this._handleClick) {
      this.galleryTarget.removeEventListener("click", this._handleClick)
    }
  }

  loadCSS() {
    if (!document.querySelector('link[href*="photoswipe.css"]')) {
      const link = document.createElement("link")
      link.rel = "stylesheet"
      link.href = "https://cdn.jsdelivr.net/npm/photoswipe@5.4.4/dist/photoswipe.css"
      document.head.appendChild(link)
    }
  }

  handleGalleryClick(e) {
    const clicked = e.target.closest("a[data-pswp-src]")
    if (!clicked) return
    e.preventDefault()

    const allLinks = Array.from(this.galleryTarget.querySelectorAll("a[data-pswp-src]"))
    const items = allLinks.map(el => this.buildItem(el))
    const index = allLinks.indexOf(clicked)

    this.open(items, index)
  }

  buildItem(el) {
    const item = {
      src: el.dataset.pswpSrc || el.href,
      width: parseInt(el.dataset.pswpWidth) || 0,
      height: parseInt(el.dataset.pswpHeight) || 0,
      alt: el.dataset.pswpAlt || "",
    }

    if (!item.width || !item.height) {
      const thumb = el.querySelector("img")
      if (thumb && thumb.complete && thumb.naturalWidth) {
        const ratio = thumb.naturalWidth / thumb.naturalHeight
        if (ratio > 1.2) {
          item.width = 2400
          item.height = Math.round(2400 / ratio)
        } else if (ratio < 0.8) {
          item.height = 2400
          item.width = Math.round(2400 * ratio)
        } else {
          item.width = 2000
          item.height = 2000
        }
      } else {
        item.width = 1920
        item.height = 1080
      }
      item.needsUpdate = true
    }

    return item
  }

  open(items, index) {
    const fieldTitle = this.fieldTitleValue

    const blurBackdrop = document.createElement("div")
    blurBackdrop.className = "pswp-blur-backdrop"
    document.body.appendChild(blurBackdrop)
    requestAnimationFrame(() => blurBackdrop.classList.add("pswp-blur-backdrop--visible"))

    const pswp = new PhotoSwipe({
      dataSource: items,
      index: index || 0,
      showHideAnimationType: "zoom",
      wheelToZoom: true,
      closeOnVerticalDrag: false,
      pinchToClose: false,
      tapAction: false,
      clickToCloseNonZoomable: false,
      padding: { top: 20, bottom: 40, left: 20, right: 20 },
      bgOpacity: 0.5,
    })

    const updateCounter = () => {
      const counter = pswp.element?.querySelector(".pswp__counter")
      if (counter && fieldTitle) {
        counter.textContent = `${fieldTitle} · ${pswp.currIndex + 1} / ${pswp.getNumItems()}`
      }
    }

    pswp.on("close", () => blurBackdrop.classList.remove("pswp-blur-backdrop--visible"))
    pswp.on("destroy", () => blurBackdrop.remove())

    // Update actual dimensions after full image loads
    pswp.on("imageSrcChange", ({ content }) => {
      if (!content.data.needsUpdate) return
      const updateDimensions = () => {
        const img = content.pictureElement?.querySelector("img")
        if (img?.naturalWidth && img?.naturalHeight) {
          content.width = img.naturalWidth
          content.height = img.naturalHeight
          content.updateContentSize(true)
        }
      }
      content.pictureElement?.addEventListener("load", updateDimensions, { once: true })
    })

    pswp.init()

    // Register AFTER init so our handler runs after PhotoSwipe's own internal change handler
    pswp.on("change", () => requestAnimationFrame(updateCounter))
    // Seed the counter for the first slide
    requestAnimationFrame(updateCounter)
  }
}
